import { useMemo } from "react";
import type { InstrumentSlot } from "../../../../../src/lib/audio";
import { useStoreSelector } from "../../../../../src/lib/store";
import {
  PRESET_CATEGORIES,
  categorySortIndex,
  type PresetCategoryId,
} from "../../lib/presetBank";
import { getLabActions } from "../../labActions";
import { projectStore } from "../../store/projectStore";
import { SearchableSelect, type SearchableSelectOption } from "../SearchableSelect";

/**
 * Bank / category controls for the synth instrument body.
 * Searchable floating selects restore the pre-rewrite preset browser UX.
 */
export function InstrumentBankBar({ slot }: { slot: InstrumentSlot }) {
  const bank = useStoreSelector(projectStore, s => s.bank);
  const presets = useStoreSelector(projectStore, s => s.presets);
  const presetName = presets[slot]?.name ?? "";
  const selected =
    bank.find(entry => entry.name === presetName)?.id
    ?? bank[0]?.id
    ?? "";

  const entry = bank.find(item => item.id === selected);

  const bankOptions = useMemo((): SearchableSelectOption[] => {
    const ordered = bank.slice().sort((a, b) => {
      const byCat = categorySortIndex(a.category) - categorySortIndex(b.category);
      if (byCat !== 0) {
        return byCat;
      }
      return a.name.localeCompare(b.name);
    });
    return ordered.map(item => ({
      value: item.id,
      label: item.name,
      group: item.category,
      hint: item.tags?.join(", "),
    }));
  }, [bank]);

  const categoryOptions = useMemo(
    (): SearchableSelectOption[] =>
      PRESET_CATEGORIES.map(cat => ({ value: cat.id, label: cat.label })),
    [],
  );

  const categoryFilters = useMemo(
    () => PRESET_CATEGORIES.map(entry => ({ id: entry.id, label: entry.label })),
    [],
  );

  return (
    <div className="flex shrink-0 flex-col gap-[0.35rem] border-b border-white/[0.06] px-2 py-1.5">
      <SearchableSelect
        className="w-full"
        ariaLabel="Preset bank"
        searchPlaceholder="Search name, category, or tag"
        emptyMessage="No presets match."
        value={selected}
        options={bankOptions}
        filters={categoryFilters}
        onChange={bankId => getLabActions()?.applyInstrumentBankEntry?.(slot, bankId)}
      />

      <div className="flex items-end gap-[0.45rem]">
        {/* items-stretch: @layer base `label { align-items: center }` shrinks the select. */}
        <label className="flex min-w-0 flex-1 flex-col items-stretch gap-0.5">
          <span className="text-[10px] tracking-[0.04em] text-slate-500 uppercase">Category</span>
          <SearchableSelect
            className="w-full"
            ariaLabel="Preset category"
            searchPlaceholder="Search categories"
            emptyMessage="No categories."
            value={entry?.category ?? "user"}
            options={categoryOptions}
            disabled={!entry || entry.builtin}
            onChange={value =>
              getLabActions()?.setInstrumentBankCategory?.(
                selected,
                value as PresetCategoryId,
              )
            }
          />
        </label>
        <button
          type="button"
          className="h-8 shrink-0 self-end border border-lab-line bg-lab-panel-deep px-[0.55rem] text-[11px] disabled:cursor-not-allowed disabled:opacity-40"
          disabled={!entry || entry.builtin}
          onClick={() => getLabActions()?.editInstrumentBankTags?.(selected)}
        >
          Tags…
        </button>
      </div>

      <div className="flex flex-wrap gap-1">
        <BankAction onClick={() => getLabActions()?.saveInstrumentBank?.(slot)}>Save</BankAction>
        <BankAction onClick={() => getLabActions()?.saveInstrumentBankAs?.(slot)}>Save as…</BankAction>
        <BankAction onClick={() => getLabActions()?.renameInstrumentBank?.(selected)}>Rename</BankAction>
        <BankAction
          disabled={!entry || entry.builtin}
          onClick={() => getLabActions()?.deleteInstrumentBank?.(selected)}
        >
          Delete
        </BankAction>
        <BankAction onClick={() => getLabActions()?.exportInstrumentBank?.(selected)}>Export</BankAction>
        <BankAction onClick={() => getLabActions()?.exportAllInstrumentBank?.()}>Export all</BankAction>
        <BankAction onClick={() => getLabActions()?.importInstrumentBank?.()}>Import…</BankAction>
      </div>
    </div>
  );
}

function BankAction({
  children,
  onClick,
  disabled,
}: {
  children: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="border border-lab-line bg-lab-panel-deep px-1.5 py-[0.22rem] text-[11px] disabled:cursor-not-allowed disabled:opacity-40"
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
